import { useDeferredValue, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveProductionDiscard,
  useProductionDiscardPendingApprovals,
} from "../hooks/useProductionDiscard";
import type { ProductionDiscardDto } from "../services/productionDiscardApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { Search } from "lucide-react";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ApproveModal = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: (notes: string) => void;
  isSubmitting: boolean;
}) => {
  const [notes, setNotes] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Discard Request</DialogTitle>
          <DialogDescription>
            Approving immediately processes the discard and deducts it from stock.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor="approvalNotes">Approval Notes (optional)</FieldLabel>
          <Textarea
            id="approvalNotes"
            rows={3}
            placeholder="Any notes for the requestor..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(notes)} disabled={isSubmitting}>
            {isSubmitting ? "Approving..." : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ProductionDiscardApprovalsPage = () => {
  const notify = useNotification();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const pendingQuery = useProductionDiscardPendingApprovals(
    pageNumber,
    pageSize,
    deferredSearch,
    "Greenhouse"
  );
  const approveMutation = useApproveProductionDiscard();

  const [selected, setSelected] = useState<ProductionDiscardDto | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const openApprove = (item: ProductionDiscardDto) => {
    setSelected(item);
    setApproveOpen(true);
  };

  const openReject = (item: ProductionDiscardDto) => {
    setSelected(item);
    setRejectOpen(true);
  };

  const handleApprove = (notes: string) => {
    if (!selected) return;
    approveMutation.mutate(
      {
        id: selected.id,
        dto: { discardRequestId: selected.id, isApproved: true, approvalNotes: notes || undefined },
      },
      {
        onSuccess: () => {
          notify.success("Discard approved and stock updated");
          setApproveOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to approve discard request"),
      }
    );
  };

  const handleReject = (reason: string) => {
    if (!selected) return;
    approveMutation.mutate(
      {
        id: selected.id,
        dto: { discardRequestId: selected.id, isApproved: false, approvalNotes: reason },
      },
      {
        onSuccess: () => {
          notify.success("Discard request rejected");
          setRejectOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to reject discard request"),
      }
    );
  };

  const rows = pendingQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Production Discard Approvals</h1>
        <p className="mt-1 text-muted-foreground">
          Review and action pending discard requests for overgrown or damaged seedlings.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="discard_requests.approve" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CardTitle>Pending Discard Requests</CardTitle>
              {(pendingQuery.data?.totalCount ?? 0) > 0 && (
                <Badge className="border-orange-400 bg-orange-100 text-orange-700">
                  {pendingQuery.data?.totalCount}
                </Badge>
              )}
            </div>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search requests..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPageNumber(1);
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {pendingQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!pendingQuery.isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No pending discard requests.
            </p>
          )}
          {rows.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Instruction #</TableHead>
                    <TableHead>Crop / Variety</TableHead>
                    <TableHead>Qty to Discard</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Requested At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium text-sm">
                        {item.greenhouseStockEntryId
                          ? `Agroforestry stock${item.instructionNumber ? ` (${item.instructionNumber})` : ""}`
                          : (item.instructionNumber ?? "—")}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.cropType}
                        {item.variety ? ` — ${item.variety}` : ""}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {item.quantityToDiscard}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.reasonName}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-muted-foreground truncate">
                        {item.reasonDetails || "—"}
                      </TableCell>
                      <TableCell className="text-sm">{item.requestedByUserName}</TableCell>
                      <TableCell className="text-sm">{formatDate(item.requestedAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => openApprove(item)}
                            disabled={approveMutation.isPending}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => openReject(item)}
                            disabled={approveMutation.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {pendingQuery.data && (
            <PaginationBar
              currentPage={pendingQuery.data.currentPage}
              pageSize={pendingQuery.data.pageSize}
              totalCount={pendingQuery.data.totalCount}
              totalPages={pendingQuery.data.totalPages}
              onPageChange={setPageNumber}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNumber(1);
              }}
            />
          )}
        </CardContent>
      </Card>

      <ApproveModal
        open={approveOpen}
        onOpenChange={setApproveOpen}
        onConfirm={handleApprove}
        isSubmitting={approveMutation.isPending}
      />

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject Discard Request"
        description="Provide a reason. The requestor will be able to see this."
        reasonLabel="Rejection Reason"
        reasonPlaceholder="e.g. Quantity seems overstated — please re-verify"
        submitLabel="Reject"
        isSubmitting={approveMutation.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
